import { useCallback, useEffect, useRef, useState } from 'react';
import { Loader2, Upload, FileText, Trash2, Download } from 'lucide-react';
import { apiClient } from '@/lib/api';
import type { Workgroup, WorkgroupFile } from './types';

interface Props {
  workgroup: Workgroup;
  canManage: boolean;
}

function fmtSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

export default function FilesTab({ workgroup, canManage }: Props) {
  const [files, setFiles] = useState<WorkgroupFile[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiClient.get<WorkgroupFile[]>(`/messenger/workgroups/${workgroup.id}/files`);
      setFiles(res.data ?? []);
    } finally {
      setLoading(false);
    }
  }, [workgroup.id]);

  useEffect(() => { load(); }, [load]);

  async function handleUpload(file: File) {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await apiClient.post(`/messenger/workgroups/${workgroup.id}/files`, fd);
      load();
    } finally {
      setUploading(false);
    }
  }

  async function handleDelete(id: string) {
    if (!confirm('Eliminare il file?')) return;
    await apiClient.delete(`/messenger/workgroups/${workgroup.id}/files/${id}`);
    load();
  }

  return (
    <div className="p-4">
      {canManage && (
        <div className="mb-4">
          <input
            type="file"
            ref={inputRef}
            className="hidden"
            onChange={(e) => e.target.files?.[0] && handleUpload(e.target.files[0])}
          />
          <button
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            className="px-4 py-2 bg-primary hover:bg-primary/90 disabled:opacity-50 rounded-lg text-primary-foreground text-sm flex items-center gap-2"
          >
            {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
            Carica file
          </button>
        </div>
      )}

      {loading && <div className="text-center py-8 text-muted-foreground"><Loader2 className="animate-spin inline" /></div>}
      {!loading && files.length === 0 && (
        <div className="text-center py-12 text-muted-foreground/80 text-sm">Nessun file condiviso</div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        {files.map(f => (
          <div key={f.id} className="bg-card border border-border rounded-lg p-3 flex items-center gap-3">
            <FileText size={20} className="text-muted-foreground flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-sm text-foreground truncate">{f.filename}</div>
              <div className="text-xs text-muted-foreground/80">
                {fmtSize(f.size)} · {new Date(f.created_at).toLocaleDateString('it-IT')}
              </div>
            </div>
            <a
              href={f.storage_path}
              target="_blank"
              rel="noopener noreferrer"
              className="text-muted-foreground hover:text-foreground p-1"
            >
              <Download size={16} />
            </a>
            {canManage && (
              <button onClick={() => handleDelete(f.id)} className="text-muted-foreground hover:text-red-400 p-1">
                <Trash2 size={16} />
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
